import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import {
  BackendDeleteResponse,
  BackendUpdateResponse,
  Category,
  Datastore,
  DatastoreDocument,
  isDefined,
  Skill,
  toNumber,
  uniqWith,
  UserSkill,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import {
  HeadingSize,
  HeadingType,
  HeadingWeight,
} from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontColor } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { debounceTime, map, startWith, take } from 'rxjs/operators';

@Component({
  template: `
    <fl-bit flTrackingSection="SkillsEditModal" class="SkillsEditModal">
      <fl-bit class="HeadingSection">
        <fl-heading
          i18n="Select skills modal header"
          [flMarginBottom]="Margin.SMALL"
          [headingType]="HeadingType.H2"
          [size]="HeadingSize.SMALL"
          [weight]="HeadingWeight.BOLD"
        >
          Select your skills and expertise
        </fl-heading>

        <fl-bit class="HeaderRow">
          <fl-bit class="SearchContent">
            <app-skills-edit-modal-search
              class="SearchInput"
              [control]="skillSearchControl"
              [flMarginRight]="Margin.MID"
              [selectedSkills]="selectedSkills$ | async"
            ></app-skills-edit-modal-search>

            <fl-text
              *ngIf="(selectedSkills$ | async)?.length > 0"
              i18n="Matching jobs message"
              [color]="FontColor.MID"
            >
              {{ matchingJobCount$ | async }} jobs matching your skills
            </fl-text>
          </fl-bit>

          <fl-button
            *ngIf="showSaveButton$ | async"
            i18n="Save skills button"
            flTrackingLabel="SaveSkillsButton"
            [busy]="busySavingSkills$ | async"
            [color]="ButtonColor.SECONDARY"
            (click)="handleSaveSkills()"
          >
            Save
          </fl-button>
        </fl-bit>
      </fl-bit>

      <fl-bit class="AlertSection">
        <fl-banner-alert
          *ngIf="overMaxSkillsLimit$ | async"
          i18n="User is over maximum skills limit error"
          [closeable]="false"
          [flMarginBottom]="Margin.MID"
          [type]="BannerAlertType.ERROR"
        >
          You can only select a maximum of
          {{ maxSkillsLimit$ | async }} skills. If you require more,
          <fl-link
            link="membership"
            flTrackingLabel="SkillsLimitUpgradeAccount"
          >
            upgrade your account.
          </fl-link>
        </fl-banner-alert>

        <fl-banner-alert
          *ngIf="errorSaving$ | async"
          i18n="Error saving skills message"
          [closeable]="false"
          [flMarginBottom]="Margin.MID"
          [type]="BannerAlertType.ERROR"
        >
          There was an issue when saving your skills. Please try again, if the
          problem persists
          <fl-link link="support" flTrackingLabel="ErrorSavingSupportLink">
            contact support.
          </fl-link>
        </fl-banner-alert>
      </fl-bit>

      <fl-grid class="ContentSection">
        <fl-col class="Categories" [col]="12" [colDesktopSmall]="3">
          <app-skills-edit-modal-categories
            class="InnerContent"
            [categories$]="categories$"
            [selectedCategory$]="selectedCategory$"
            (selectedCategory)="handleSelectedCategory($event)"
          ></app-skills-edit-modal-categories>
        </fl-col>
        <fl-col [col]="12" [colDesktopSmall]="6">
          <app-skills-edit-modal-selection
            class="InnerContent"
            [filteredSkills]="filteredSkills$ | async"
            [searchText]="skillSearchControl.value"
            [loading]="loadingSearchedForSkills$ | async"
            [selectedCategory]="selectedCategory$ | async"
            [skillsFormGroup]="skillFormGroup"
            (deselectedSkill)="handleDeselectedSkill($event)"
            (selectedSkill)="handleSelectedSkill($event)"
          ></app-skills-edit-modal-selection>
        </fl-col>
        <fl-col class="Selected" [col]="12" [colDesktopSmall]="3">
          <app-skills-edit-modal-selected
            class="InnerContent"
            [selectedSkills]="selectedSkills$ | async"
            (deselectedSkill)="handleDeselectedSkillSelection($event)"
          ></app-skills-edit-modal-selected>
        </fl-col>
      </fl-grid>
    </fl-bit>
  `,
  styleUrls: ['./skills-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillsEditModalComponent implements OnInit, OnDestroy {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  FontColor = FontColor;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  HeadingWeight = HeadingWeight;
  Margin = Margin;

  private readonly OTHERS_CATEGORY: Category = { name: 'Others', id: 99 };

  private _busySavingSkills$ = new Rx.Subject<boolean>();
  private _changesMade$ = new Rx.BehaviorSubject<boolean>(false);
  private _errorSaving$ = new Rx.Subject<boolean>();
  private _selectedCategory$ = new Rx.Subject<Category>();
  private _selectedSkills$ = new Rx.BehaviorSubject<ReadonlyArray<Skill>>([]);
  private _loadingSearchedForSkills$ = new Rx.Subject<boolean>();

  busySavingSkills$ = this._busySavingSkills$.asObservable();
  errorSaving$ = this._errorSaving$.asObservable();
  selectedCategory$ = this._selectedCategory$.asObservable();
  selectedSkills$ = this._selectedSkills$.asObservable();
  loadingSearchedForSkills$ = this._loadingSearchedForSkills$.asObservable();

  userSkillsDocument: DatastoreDocument<'userSkills'>;
  formSetupSubscription: Rx.Subscription;

  skillFormGroup = new FormGroup({});
  skillSearchControl = new FormControl('');

  categories$: Rx.Observable<ReadonlyArray<Category>>;
  filteredSkills$: Rx.Observable<ReadonlyArray<Skill>>;
  matchingJobCount$: Rx.Observable<number>;
  maxSkillsLimit$: Rx.Observable<number>;
  overMaxSkillsLimit$: Rx.Observable<boolean>;
  showSaveButton$: Rx.Observable<boolean>;
  userId$: Rx.Observable<number>;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private fb: FormBuilder,
    private modalRef: ModalRef<SkillsEditModalComponent>,
  ) {}

  ngOnInit() {
    const allSkillsCollection$ = this.datastore.collection('skills');
    this.userSkillsDocument = this.datastore.document('userSkills');
    const membershipBenefitsDocument$ = this.datastore.document(
      'membershipBenefits',
      'skills_limit',
    );

    this.userId$ = this.auth.getUserId().pipe(map(id => toNumber(id)));

    // Set a form group of all skills and pre-fill the selected skills.
    this.formSetupSubscription = Rx.combineLatest([
      this.userSkillsDocument.valueChanges(),
      allSkillsCollection$.valueChanges(),
    ]).subscribe(([usersSkills, skills]) => {
      this.skillFormGroup.reset();
      const selectedSkills: Skill[] = [];

      skills.forEach(skill => {
        const hasSkill = usersSkills.skills.includes(skill.id);
        if (hasSkill) {
          selectedSkills.push(skill);
        }

        this.skillFormGroup.addControl(skill.seoUrl, this.fb.control(hasSkill));
      });

      this._selectedSkills$.next(selectedSkills);
    });

    this.maxSkillsLimit$ = membershipBenefitsDocument$
      .valueChanges()
      .pipe(map(benefit => benefit.value));

    this.overMaxSkillsLimit$ = Rx.combineLatest([
      this.maxSkillsLimit$,
      this._selectedSkills$,
    ]).pipe(
      map(([maxSkills, selectedSkills]) => selectedSkills.length > maxSkills),
    );

    this.skillSearchControl.valueChanges.subscribe(() => {
      this._loadingSearchedForSkills$.next(true);
    });

    this.showSaveButton$ = Rx.combineLatest([
      this.overMaxSkillsLimit$,
      this._changesMade$,
    ]).pipe(
      map(
        ([overMaxSkillCount, changesMade]) => !overMaxSkillCount && changesMade,
      ),
    );

    this.matchingJobCount$ = this.selectedSkills$.pipe(
      map(skills =>
        skills.reduce((prev, cur) => prev + cur.activeProjectCount, 0),
      ),
    );

    this.filteredSkills$ = Rx.combineLatest([
      allSkillsCollection$.valueChanges(),
      this.selectedCategory$.pipe(startWith(undefined)),
      this.skillSearchControl.valueChanges.pipe(
        startWith(undefined),
        debounceTime(500),
      ),
    ]).pipe(
      map(([skills, selectedCategory, searchInputText]) => {
        // If the user has used the skill search input, prioritise over the
        // selected category. Search on both the skill name and category name.
        if (searchInputText) {
          const searchTerm = searchInputText.toLowerCase();
          const filteredSkills = skills.filter(
            skill =>
              (skill.category &&
                skill.category.name &&
                skill.category.name.toLowerCase().includes(searchTerm)) ||
              skill.name.toLowerCase().includes(searchTerm),
          );
          this._loadingSearchedForSkills$.next(false);
          return filteredSkills;
        }
        // If a category has been selected return all skills with that category.
        if (selectedCategory) {
          const filteredSkills = skills.filter(
            skill =>
              skill.category && skill.category.id === selectedCategory.id,
          );
          this._loadingSearchedForSkills$.next(false);

          return filteredSkills;
        }

        this._loadingSearchedForSkills$.next(false);
        // The user has not selected a category or used the search box.
        return [];
      }),
    );

    this.categories$ = allSkillsCollection$.valueChanges().pipe(
      // Take only unique categories from all active skills.
      map(skills => [
        ...uniqWith(
          skills.map(skill => skill.category).filter(isDefined),
          (t1, t2) => t1.id === t2.id,
        )
          // Remove the 'Others' category and order by id.
          .filter(category => category.id !== this.OTHERS_CATEGORY.id)
          .sort((a, b) => a.id - b.id),
        // Appends the 'Others' category to the end of the list.
        this.OTHERS_CATEGORY,
      ]),
    );
  }

  handleSelectedCategory(selectedCategory: Category): void {
    this._selectedCategory$.next(selectedCategory);

    // Clear out the input text when selecting a category.
    this.skillSearchControl.reset(undefined);
  }

  handleSelectedSkill(selectedSkill: Skill): void {
    this._changesMade$.next(true);

    this.selectedSkills$
      .pipe(take(1))
      .toPromise()
      .then(skills => {
        this._selectedSkills$.next([...skills, selectedSkill]);
      });
  }

  handleDeselectedSkill(deselectedSkill: Skill): void {
    this._changesMade$.next(true);

    this.selectedSkills$
      .pipe(take(1))
      .toPromise()
      .then(skills => {
        this._selectedSkills$.next(
          skills.filter(skill => skill !== deselectedSkill),
        );
      });
  }

  handleSaveSkills(): void {
    this._busySavingSkills$.next(true);
    this._errorSaving$.next(false);

    Rx.combineLatest([this.userId$, this.selectedSkills$])
      .pipe(take(1))
      .toPromise()
      .then(([userId, selectedSkills]) => {
        const skillIds = selectedSkills.map(skill => skill.id);
        const userSkill: UserSkill = {
          id: userId,
          skills: skillIds,
        };

        // Only update the skills if the user has at least 1 skill selected.
        if (skillIds.length > 0) {
          this.userSkillsDocument
            .update(userSkill)
            .then(response => this.handleSaveResponse(response));
        } else {
          this.userSkillsDocument
            .remove()
            .then(response => this.handleSaveResponse(response));
        }
      });
  }

  handleSaveResponse(
    response:
      | BackendDeleteResponse<'userSkills'>
      | BackendUpdateResponse<'userSkills'>,
  ): void {
    this._busySavingSkills$.next(false);
    if (response.status === 'error') {
      // TODO: implement a toast error message when live.
      this._errorSaving$.next(true);
      return;
    }

    // Close the modal with a boolean of if any changes were made. This is used
    // to determine if the user profile top skills needs to be updated.
    this.modalRef.close(this._changesMade$.getValue());
  }

  handleDeselectedSkillSelection(deselectedSkill: Skill): void {
    const skill = this.skillFormGroup.get(deselectedSkill.seoUrl);
    if (skill) {
      skill.setValue(null);
    }
    this.handleDeselectedSkill(deselectedSkill);
  }

  ngOnDestroy(): void {
    if (this.formSetupSubscription) {
      this.formSetupSubscription.unsubscribe();
    }
  }
}
